import React, { useEffect } from 'react'
import { AdOverviewAd, GetAdOverviewAdsApiArg, useGetAdOverviewAdsQuery } from '../../../../app/api'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import {
  selectAdOverviewAdsExpanded,
  selectAdOverviewAdsParams,
  selectAdOverviewFilters,
  setAdsQueryParams,
  setAllowDecloak,
} from '../../model/adOverviewSlice'
import { IAdOverviewFilter } from '../../model/types/AdOverviewFilter.interface'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { ColumnsType } from 'antd/lib/table'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { LinksList } from '../link-list/LinksList'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { IWithExpandedAllProps, withExpanded } from '../../../../shared/lib/hoc/WithExpanded'
import { useTranslation } from 'react-i18next'
import { DetailsCell } from '../../ui/cells/DetailsCell'
import { KeywordsCell } from '../../ui/cells/KeywordsCell'
import { AdCell } from '../../ui/cells/AdCell'
import { RedirectLinksCell } from '../../ui/cells/RedirectLinksCell'

const Table = ({
  onToggleExpand,
  isExpandAll,
  expandedRowKeys,
  onToggleExpandAll,
}: IWithExpandedAllProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isExpanded = useAppSelector(selectAdOverviewAdsExpanded)
  const adOverviewFilter = useAppSelector<IAdOverviewFilter>(selectAdOverviewFilters)
  const {
    project,
    dateRange: date,
    country,
    adDomain: site,
    search,
    engine,
    browser,
    city,
    policy,
    affiliate,
    is_decloak,
    group,
    advertiserName,
    reversed_filters,
  } = adOverviewFilter

  const queryParams = useAppSelector(selectAdOverviewAdsParams)
  const { data, isLoading, isFetching } = useGetAdOverviewAdsQuery(
    queryParams as GetAdOverviewAdsApiArg,
    {
      skip: !project || queryParams == undefined,
    },
  )

  useEffect(() => {
    if (!project) return

    dispatch(
      setAdsQueryParams({
        page: 1,
        'per-page': 10,
        projectId: +project,
        countryId: country || undefined,
        date,
        search,
        site,
        engineId: engine || undefined,
        browserId: browser || undefined,
        cityId: city || undefined,
        policy: policy as GetAdOverviewAdsApiArg['policy'],
        affiliateId: affiliate || undefined,
        advertiserId: advertiserName || undefined,
        isDecloak: is_decloak,
        group,
        isShoppingAds: true,
        reversedFilters: (reversed_filters || undefined) as unknown as string[],
      }),
    )
  }, [adOverviewFilter])

  useEffect(() => {
    if (onToggleExpandAll) onToggleExpandAll(isExpanded)
  }, [isExpanded])

  useEffect(() => {
    if (data?.data?.allow_decloak !== undefined) {
      dispatch(setAllowDecloak(data.data.allow_decloak))
    }
  }, [data])

  const columns: ColumnsType<AdOverviewAd> = [
    {
      title: t('table.columns.ad'),
      key: 'text',
      render: (_, record) => <AdCell {...record} />,
      width: '400px',
    },
    {
      title: t('table.columns.keyword(s)'),
      dataIndex: 'keywords',
      key: 'keywords',
      width: '150px',
      render: (keywords: string) => <KeywordsCell keywords={keywords} />,
    },
    {
      title: t('table.columns.redirectLinks'),
      dataIndex: 'redirects',
      key: 'redirects',
      width: '150px',
      render: (_, record) => <RedirectLinksCell {...record} onToggleExpand={onToggleExpand} />,
    },
    {
      title: t('table.columns.position'),
      dataIndex: 'position_global',
      key: 'position_global',
      width: '100px',
      sorter: true,
    },
    {
      title: t('table.columns.details'),
      key: 'details',
      width: '320px',
      render: (_, record) => <DetailsCell {...record} />,
    },
    {
      title: t('table.columns.found'),
      dataIndex: 'last_found',
      key: 'last_found',
      width: 150,
      sorter: true,
    },
  ]

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?: SorterResult<AdOverviewAd> | SorterResult<AdOverviewAd>[],
  ) => {
    if (queryParams) {
      dispatch(
        setAdsQueryParams({
          ...queryParams,
          page: current,
          'per-page': pageSize,
          sort: useSorter(sorter),
        }),
      )
    }
  }

  return (
    <BpTable
      expandable={{
        expandedRowRender: (record) => <LinksList {...record} className='max-w-[980px]' />,
        expandedRowKeys: isExpandAll
          ? data?.data?.items?.map((item) => `${item.id}`)
          : expandedRowKeys,
        showExpandColumn: false,
        expandedRowClassName: () => 'links-list',
      }}
      columns={columns}
      data={data as TTableResponse<AdOverviewAd>}
      dataSource={data?.data?.items}
      loading={isLoading || isFetching}
      rowClassName={(e) => (e.is_decloak ? 'cloaca' : '')}
      rowKey={(record) => `${record.id}`}
      onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
    ></BpTable>
  )
}

export const ShoppingAdsTable = withExpanded(Table)
